import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import LeadForm from '@/components/LeadForm';
import SEO from '@/components/SEO';
import { API_URL } from '@/lib/utils';
import { 
  Shield, Award, FlaskConical, GraduationCap, Laptop, Building, 
  FileCheck, Presentation, ChevronRight, Star, Phone, ArrowRight,
  Users, Clock, CheckCircle, Trophy, Download, FileText
} from 'lucide-react';

const iconMap = {
  Shield, Award, FlaskConical, GraduationCap, Laptop, Building, FileCheck, Presentation
};

// High-quality healthcare images
const IMAGES = {
  hero: "https://images.unsplash.com/photo-1551076805-e1869033e561?w=1200&h=800&fit=crop",
  doctors: "https://images.pexels.com/photos/5452193/pexels-photo-5452193.jpeg?auto=compress&cs=tinysrgb&w=1200",
  hospital: "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=1200&h=800&fit=crop",
  consulting: "https://images.pexels.com/photos/5452285/pexels-photo-5452285.jpeg?auto=compress&cs=tinysrgb&w=1200",
  digital: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=1200&h=800&fit=crop",
  whyUs: "https://images.unsplash.com/photo-1538108149393-fbbd81895907?w=1200&h=800&fit=crop",
  medicalTeam: "https://images.pexels.com/photos/4173251/pexels-photo-4173251.jpeg?auto=compress&cs=tinysrgb&w=1200",
  clinic: "https://images.pexels.com/photos/247786/pexels-photo-247786.jpeg?auto=compress&cs=tinysrgb&w=1200",
  quality: "https://images.pexels.com/photos/3938022/pexels-photo-3938022.jpeg?auto=compress&cs=tinysrgb&w=1200",
  technology: "https://images.pexels.com/photos/7723528/pexels-photo-7723528.jpeg?auto=compress&cs=tinysrgb&w=940"
};

const CAREO_LOGO = "https://customer-assets.emergentagent.com/job_steward-health-hub/artifacts/di9kfck2_53561592_transparent11%20-%20Copy.png";

const Home = () => {
  const [services, setServices] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [products, setProducts] = useState([]);
  const [clients, setClients] = useState([]);
  const [partners, setPartners] = useState([]);
  const [brochures, setBrochures] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [academicPartners, setAcademicPartners] = useState([]);
  const [siteStats, setSiteStats] = useState([
    { value: '200+', label: 'Healthcare Facilities', icon: Building },
    { value: '8+', label: 'Years Experience', icon: Clock },
    { value: '100%', label: 'Success Rate', icon: Trophy },
    { value: '15+', label: 'Expert Consultants', icon: Users },
  ]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, testimonialsRes, productsRes, clientsRes, partnersRes, brochuresRes, announcementsRes, academicRes, settingsRes] = await Promise.all([
          axios.get(`${API_URL}/services?active_only=true`),
          axios.get(`${API_URL}/testimonials?active_only=true`),
          axios.get(`${API_URL}/products`),
          axios.get(`${API_URL}/partners?active_only=true&type=client`),
          axios.get(`${API_URL}/partners?active_only=true&type=partner`),
          axios.get(`${API_URL}/brochures?active_only=true`),
          axios.get(`${API_URL}/announcements?active_only=true`),
          axios.get(`${API_URL}/academic-partners?active_only=true`),
          axios.get(`${API_URL}/settings`)
        ]);
        setServices(servicesRes.data.slice(0, 6));
        setTestimonials(testimonialsRes.data);
        setProducts(productsRes.data);
        setClients(clientsRes.data);
        setPartners(partnersRes.data);
        setBrochures(brochuresRes.data);
        setAnnouncements(announcementsRes.data);
        setAcademicPartners(academicRes.data);
        
        const s = settingsRes.data;
        setSiteStats([
          { value: s.stat_facilities || '200+', label: s.stat_facilities_label || 'Healthcare Facilities', icon: Building },
          { value: s.stat_experience_years || '8+', label: s.stat_experience_label || 'Years Experience', icon: Clock },
          { value: s.stat_success_rate || '100%', label: s.stat_success_label || 'Success Rate', icon: Trophy },
          { value: s.stat_consultants || '15+', label: s.stat_consultants_label || 'Expert Consultants', icon: Users },
        ]);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  return (
    <div data-testid="home-page">
      <SEO 
        title="NABH Consultancy & Digital Healthcare Solutions"
        description="Steward Healthcare India - Leading healthcare consulting company. Provisional NABH Mitra & Digital Mitra certified. Offering NABH accreditation, CareO EMR/HIS, hospital planning, and healthcare courses. Serving 200+ facilities since 2016."
        keywords="NABH consultancy India, NABH accreditation, hospital accreditation, healthcare consulting, EMR software India, CareO, HIS software, NABL consultancy, digital healthcare, Panipat"
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-50 via-white to-blue-50 hero-pattern overflow-hidden">
        <div className="absolute inset-0 opacity-50 pointer-events-none" style={{backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%231a2a5e' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")"}} />
        
        <div className="max-w-7xl mx-auto px-4 py-16 md:py-24 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left animate-fade-in-up">
              <div className="inline-flex items-center gap-2 bg-[#1a2a5e]/10 text-[#1a2a5e] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <span className="w-2 h-2 bg-[#2eb24c] rounded-full animate-pulse" />
                Trusted Healthcare Partner Since 2016
              </div>
              
              <h1 className="font-manrope text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1a2a5e] leading-tight mb-6">
                Your Trusted Partner in{' '}
                <span className="text-[#2eb24c]">Healthcare Excellence</span>
              </h1>
              
              <p className="text-lg text-gray-600 mb-8 max-w-xl mx-auto lg:mx-0">
                Comprehensive healthcare consulting services including NABH accreditation, digital healthcare solutions, training programs, and hospital planning.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button asChild size="lg" className="bg-[#1a2a5e] hover:bg-[#2d4a8e] h-14 px-8 text-base">
                  <Link to="/contact" data-testid="hero-consultation-btn">
                    Get Free Consultation
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="h-14 px-8 border-2 border-[#1a2a5e] text-[#1a2a5e] hover:bg-[#1a2a5e] hover:text-white">
                  <a href="tel:+919616298162" data-testid="hero-call-btn">
                    <Phone className="mr-2 h-5 w-5" />
                    Call: +91 9616298162
                  </a>
                </Button>
              </div>
              
              {/* Trust badges */}
              <div className="mt-10 space-y-3">
                <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4">
                  <div className="flex items-center gap-2 text-sm text-gray-700 bg-green-50 px-3 py-2 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-[#2eb24c]" />
                    Provisional NABH Mitra for NABH Consultancy
                  </div>
                </div>
                <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4">
                  <div className="flex items-center gap-2 text-sm text-gray-700 bg-blue-50 px-3 py-2 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-[#3b7dd8]" />
                    Provisional NABH Digital Mitra for Digital NABH Consultancy
                  </div>
                </div>
                <p className="text-xs text-gray-500 text-center lg:text-left">(Mr. Dinesh Kumar - Certified Consultant)</p>
              </div>
            </div>
            
            {/* Lead Form */}
            <div className="lg:pl-8">
              <Card className="shadow-2xl border-0 overflow-hidden">
                <div className="bg-[#1a2a5e] text-white p-6">
                  <h3 className="font-manrope text-xl font-bold">Schedule a Free Consultation</h3>
                  <p className="text-blue-100 text-sm mt-1">Our experts will guide you through the process</p>
                </div>
                <CardContent className="p-6">
                  <LeadForm source="hero" />
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-[#1a2a5e] py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {siteStats.map((stat, index) => (
              <div key={index} className="text-center text-white">
                <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <stat.icon className="h-6 w-6 text-[#2eb24c]" />
                </div>
                <div className="text-3xl md:text-4xl font-bold font-manrope">{stat.value}</div>
                <div className="text-blue-200 text-sm mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Announcements Panel Section */}
      {announcements.length > 0 && (
        <section className="py-12 bg-white" data-testid="announcements-section">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Latest Updates Text */}
              <div className="lg:col-span-2">
                <div className="mb-6">
                  <span className="text-[#2eb24c] font-semibold text-sm uppercase tracking-wider">Latest</span>
                  <h2 className="font-manrope text-3xl md:text-4xl font-bold text-[#1a2a5e] mt-2">
                    Healthcare Excellence in Action
                  </h2>
                  <p className="text-gray-600 mt-3">Trusted by leading healthcare facilities across India for quality consultancy and digital transformation.</p>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="col-span-2 row-span-2">
                    <img src={IMAGES.medicalTeam} alt="Medical team consulting" className="w-full h-full object-cover rounded-2xl shadow-lg hover:shadow-xl transition-shadow" style={{minHeight: '350px'}} />
                  </div>
                  <div>
                    <img src={IMAGES.clinic} alt="Modern clinic" className="w-full h-40 object-cover rounded-xl shadow-lg hover:shadow-xl transition-shadow" />
                  </div>
                  <div>
                    <img src={IMAGES.quality} alt="Quality management" className="w-full h-40 object-cover rounded-xl shadow-lg hover:shadow-xl transition-shadow" />
                  </div>
                  <div>
                    <img src={IMAGES.hospital} alt="Hospital facility" className="w-full h-40 object-cover rounded-xl shadow-lg hover:shadow-xl transition-shadow" />
                  </div>
                  <div>
                    <img src={IMAGES.technology} alt="Healthcare technology" className="w-full h-40 object-cover rounded-xl shadow-lg hover:shadow-xl transition-shadow" />
                  </div>
                </div>
              </div>

              {/* Announcements Panel - NABH Style */}
              <div className="lg:col-span-1">
                <div className="border border-[#d0ddf0] rounded-xl overflow-hidden bg-white shadow-sm" data-testid="announcements-panel">
                  <div className="bg-[#1a2a5e] px-5 py-4">
                    <h3 className="font-manrope text-lg font-bold text-white">Announcements</h3>
                  </div>
                  <div className="max-h-[420px] overflow-y-auto announcements-scroll">
                    {announcements.map((ann, index) => (
                      <div key={ann.id} className={`px-5 py-4 ${index !== announcements.length - 1 ? 'border-b border-gray-200' : ''}`}>
                        <div className="flex items-start gap-3">
                          <div className="w-2.5 h-2.5 rounded-full bg-[#3b7dd8] mt-1.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            {ann.link_url ? (
                              <a
                                href={ann.link_url}
                                target={ann.link_url.startsWith('/') ? '_self' : '_blank'}
                                rel="noopener noreferrer"
                                className="text-[#1a5fb4] hover:text-[#1a2a5e] hover:underline text-sm font-medium leading-snug block"
                                data-testid={`announcement-link-${ann.id}`}
                              >
                                {ann.title}
                              </a>
                            ) : (
                              <p className="text-[#1a5fb4] text-sm font-medium leading-snug">{ann.title}</p>
                            )}
                            <p className="text-xs text-gray-400 mt-1.5">
                              {new Date(ann.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Image Gallery Section (shown only if no announcements) */}
      {announcements.length === 0 && (
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-10">
            <span className="text-[#2eb24c] font-semibold text-sm uppercase tracking-wider">Our Work</span>
            <h2 className="font-manrope text-3xl font-bold text-[#1a2a5e] mt-2">
              Healthcare Excellence in Action
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="col-span-2 row-span-2">
              <img 
                src={IMAGES.medicalTeam} 
                alt="Medical team consulting" 
                className="w-full h-full object-cover rounded-2xl shadow-lg hover:shadow-xl transition-shadow"
                style={{minHeight: '400px'}}
              />
            </div>
            <div>
              <img 
                src={IMAGES.clinic} 
                alt="Modern clinic" 
                className="w-full h-48 object-cover rounded-xl shadow-lg hover:shadow-xl transition-shadow"
              />
            </div>
            <div>
              <img 
                src={IMAGES.quality} 
                alt="Quality management" 
                className="w-full h-48 object-cover rounded-xl shadow-lg hover:shadow-xl transition-shadow"
              />
            </div>
            <div>
              <img 
                src={IMAGES.hospital} 
                alt="Hospital facility" 
                className="w-full h-48 object-cover rounded-xl shadow-lg hover:shadow-xl transition-shadow"
              />
            </div>
            <div>
              <img 
                src={IMAGES.digital} 
                alt="Digital healthcare" 
                className="w-full h-48 object-cover rounded-xl shadow-lg hover:shadow-xl transition-shadow"
              />
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Services Section */}
      <section className="py-20 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-[#2eb24c] font-semibold text-sm uppercase tracking-wider">Our Services</span>
            <h2 className="font-manrope text-3xl md:text-4xl font-bold text-[#1a2a5e] mt-2">
              Comprehensive Healthcare Solutions
            </h2>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
              From accreditation consulting to digital transformation, we provide end-to-end healthcare solutions
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon] || Shield;
              return (
                <Link 
                  key={service.id} 
                  to={`/services/${service.slug}`}
                  className="group"
                  data-testid={`service-card-${service.slug}`}
                >
                  <Card className="h-full border border-gray-100 hover:border-[#1a2a5e]/20 hover:shadow-xl transition-all duration-300 service-card">
                    <CardContent className="p-6">
                      <div className="service-icon mb-4 text-[#1a2a5e] group-hover:text-white">
                        <IconComponent className="h-7 w-7" />
                      </div>
                      <h3 className="font-manrope font-bold text-lg text-[#1a2a5e] mb-2 group-hover:text-[#2eb24c] transition-colors">
                        {service.title}
                      </h3>
                      <p className="text-gray-600 text-sm leading-relaxed mb-4">
                        {service.short_description}
                      </p>
                      <span className="inline-flex items-center text-[#1a2a5e] font-medium text-sm group-hover:text-[#2eb24c]">
                        Learn More <ChevronRight className="h-4 w-4 ml-1 transition-transform group-hover:translate-x-1" />
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
          
          <div className="text-center mt-10">
            <Button asChild variant="outline" size="lg" className="border-2 border-[#1a2a5e] text-[#1a2a5e] hover:bg-[#1a2a5e] hover:text-white">
              <Link to="/services">
                View All Services <ChevronRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-20 bg-slate-50" data-testid="products-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <img src={CAREO_LOGO} alt="CareO by Steward" className="h-16 mx-auto mb-4" />
            <span className="text-[#2eb24c] font-semibold text-sm uppercase tracking-wider">Our Products</span>
            <h2 className="font-manrope text-3xl md:text-4xl font-bold text-[#1a2a5e] mt-2">
              Digital Healthcare Solutions
            </h2>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
              Transform your healthcare facility with our advanced EMR and Hospital Information Systems
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {products.map((product, index) => (
              <Card key={product.id} className="overflow-hidden border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-0">
                  <div className={`p-6 text-white relative ${index === 0 ? 'bg-gradient-to-r from-[#3b7dd8] to-[#2eb24c]' : 'bg-gradient-to-r from-[#1a2a5e] to-[#3b7dd8]'}`}>
                    <img src={CAREO_LOGO} alt="CareO" className="h-10 mb-3 brightness-0 invert" />
                    <h3 className="font-manrope text-2xl font-bold">{product.name}</h3>
                    <p className="text-white/80 mt-1">{product.tagline}</p>
                  </div>
                  <div className="p-6">
                    <p className="text-gray-600 mb-6">{product.description}</p>
                    <div className="grid grid-cols-2 gap-2 mb-6">
                      {product.features.slice(0, 6).map((feature, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm text-gray-700">
                          <CheckCircle className="h-4 w-4 text-[#2eb24c] flex-shrink-0" />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                    <Button asChild className="w-full bg-[#2eb24c] hover:bg-[#259a40]">
                      <Link to="/products" data-testid={`product-demo-${product.slug}`}>
                        Request Demo <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#2eb24c] font-semibold text-sm uppercase tracking-wider">Why Choose Us</span>
              <h2 className="font-manrope text-3xl md:text-4xl font-bold text-[#1a2a5e] mt-2 mb-6">
                India's Leading Healthcare Consulting Company
              </h2>
              <p className="text-gray-600 mb-8">
                Since 2016, we have helped 200+ healthcare facilities achieve accreditation and operational excellence with our team of 15+ expert consultants.
              </p>
              
              <div className="space-y-4">
                {[
                  { title: 'Expert Team', desc: '15+ certified healthcare consultants and quality professionals' },
                  { title: 'End-to-End Solutions', desc: 'From consulting to implementation and ongoing support' },
                  { title: 'Proven Track Record', desc: '100% success rate in NABH and NABL accreditations' },
                  { title: 'Pan-India Presence', desc: 'Serving healthcare facilities across all states' },
                ].map((item, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#2eb24c]/10 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="h-5 w-5 text-[#2eb24c]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-[#1a2a5e]">{item.title}</h4>
                      <p className="text-gray-600 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <img 
                src={IMAGES.whyUs}
                alt="Healthcare professionals" 
                className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-[#2eb24c] rounded-full flex items-center justify-center">
                    <Trophy className="h-7 w-7 text-white" />
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-[#1a2a5e]">200+</div>
                    <div className="text-gray-600 text-sm">Successful Projects</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      {testimonials.length > 0 && (
        <section className="py-20 bg-slate-50" data-testid="testimonials-section">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <span className="text-[#2eb24c] font-semibold text-sm uppercase tracking-wider">Testimonials</span>
              <h2 className="font-manrope text-3xl md:text-4xl font-bold text-[#1a2a5e] mt-2">
                What Our Clients Say
              </h2>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              {testimonials.map((testimonial) => (
                <Card key={testimonial.id} className="border-0 shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex gap-1 mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                    <p className="text-gray-600 mb-6 italic">"{testimonial.content}"</p>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-[#1a2a5e] flex items-center justify-center text-white font-bold">
                        {testimonial.name.charAt(0)}
                      </div>
                      <div>
                        <div className="font-semibold text-[#1a2a5e]">{testimonial.name}</div>
                        <div className="text-sm text-gray-500">{testimonial.designation}, {testimonial.organization}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Our Clients Section */}
      {clients.length > 0 && (
        <section className="py-16 bg-white" data-testid="clients-section">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-10">
              <span className="text-[#2eb24c] font-semibold text-sm uppercase tracking-wider">Trusted By</span>
              <h2 className="font-manrope text-3xl md:text-4xl font-bold text-[#1a2a5e] mt-2">
                Our Clients
              </h2>
              <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
                Trusted by leading healthcare facilities across India
              </p>
            </div>

            <div className="relative overflow-hidden">
              <div className="flex animate-scroll gap-12 items-center py-4">
                {[...clients, ...clients].map((client, i) => (
                  <div key={`${client.id}-${i}`} className="flex-shrink-0 group" data-testid={`client-logo-${i}`}>
                    {client.website ? (
                      <a href={client.website} target="_blank" rel="noopener noreferrer">
                        <img
                          src={client.logo_url}
                          alt={client.name}
                          className="h-16 w-auto max-w-[180px] object-contain grayscale hover:grayscale-0 transition-all duration-300 opacity-70 hover:opacity-100"
                        />
                      </a>
                    ) : (
                      <img
                        src={client.logo_url}
                        alt={client.name}
                        className="h-16 w-auto max-w-[180px] object-contain grayscale hover:grayscale-0 transition-all duration-300 opacity-70 hover:opacity-100"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Our Partners Section */}
      {partners.length > 0 && (
        <section className="py-16 bg-slate-50" data-testid="partners-section">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-10">
              <span className="text-[#2eb24c] font-semibold text-sm uppercase tracking-wider">Collaborations</span>
              <h2 className="font-manrope text-3xl md:text-4xl font-bold text-[#1a2a5e] mt-2">
                Our Partners
              </h2>
              <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
                Strategic partnerships driving healthcare innovation
              </p>
            </div>

            <div className="relative overflow-hidden">
              <div className="flex animate-scroll gap-12 items-center py-4">
                {[...partners, ...partners].map((partner, i) => (
                  <div key={`${partner.id}-${i}`} className="flex-shrink-0 group" data-testid={`partner-logo-${i}`}>
                    {partner.website ? (
                      <a href={partner.website} target="_blank" rel="noopener noreferrer">
                        <img
                          src={partner.logo_url}
                          alt={partner.name}
                          className="h-16 w-auto max-w-[180px] object-contain grayscale hover:grayscale-0 transition-all duration-300 opacity-70 hover:opacity-100"
                        />
                      </a>
                    ) : (
                      <img
                        src={partner.logo_url}
                        alt={partner.name}
                        className="h-16 w-auto max-w-[180px] object-contain grayscale hover:grayscale-0 transition-all duration-300 opacity-70 hover:opacity-100"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Academic Partnership Section */}
      {academicPartners.length > 0 && (
        <section className="py-16 bg-white" data-testid="academic-partners-section">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <span className="text-[#2eb24c] font-semibold text-sm uppercase tracking-wider">Academic Excellence</span>
              <h2 className="font-manrope text-3xl md:text-4xl font-bold text-[#1a2a5e] mt-2">
                Academic Partnership
              </h2>
            </div>
            <div className="space-y-16">
              {academicPartners.map((ap, index) => (
                <div key={ap.id} className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-10`} data-testid={`academic-partner-${ap.id}`}>
                  {ap.image_url && (
                    <div className="lg:w-1/2">
                      <img src={ap.image_url} alt={ap.title} className="rounded-2xl shadow-xl w-full h-[350px] object-cover" />
                    </div>
                  )}
                  <div className={ap.image_url ? 'lg:w-1/2' : 'w-full max-w-3xl mx-auto'}>
                    <h3 className="font-manrope text-2xl font-bold text-[#1a2a5e] mb-4">{ap.title}</h3>
                    {ap.description && (
                      <p className="text-gray-600 leading-relaxed mb-6">{ap.description}</p>
                    )}
                    {ap.features?.length > 0 && (
                      <div className="grid sm:grid-cols-2 gap-3 mb-6">
                        {ap.features.map((f, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm">
                            <div className="w-5 h-5 rounded-full bg-[#2eb24c]/10 flex items-center justify-center flex-shrink-0">
                              <svg className="w-3 h-3 text-[#2eb24c]" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                            </div>
                            <span className="text-gray-700">{f}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {ap.button_text && ap.button_link && (
                      <a href={ap.button_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#1a2a5e] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#2d4a8e] transition-colors">
                        {ap.button_text}
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Certificate & Marksheet Samples */}
            <div className="mt-16" data-testid="certificate-samples-section">
              <div className="text-center mb-10">
                <h3 className="font-manrope text-2xl md:text-3xl font-bold text-[#1a2a5e]">
                  Sample Certificate & Marksheet
                </h3>
                <p className="text-gray-600 mt-2 max-w-2xl mx-auto">
                  Students receive UGC-recognized certificates and detailed marksheets from Medhavi Skills University upon successful completion
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="group cursor-pointer" data-testid="sample-certificate-card">
                  <div className="bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 group-hover:-translate-y-1">
                    <div className="p-3 bg-gradient-to-r from-[#1a2a5e] to-[#2d4a8e]">
                      <h4 className="text-white text-center font-semibold text-base">Certificate Sample</h4>
                    </div>
                    <a href="https://customer-assets-39nsmqrw.emergentagent.net/job_87c0864c-f08f-4236-ae3c-b73a794502ae/artifacts/jep7u0i8_Sample%20Certificate.png" target="_blank" rel="noopener noreferrer">
                      <img 
                        src="https://customer-assets-39nsmqrw.emergentagent.net/job_87c0864c-f08f-4236-ae3c-b73a794502ae/artifacts/jep7u0i8_Sample%20Certificate.png" 
                        alt="Sample Certificate - Medhavi Skills University" 
                        className="w-full h-auto object-contain p-4"
                        loading="lazy"
                      />
                    </a>
                  </div>
                </div>
                <div className="group cursor-pointer" data-testid="sample-marksheet-card">
                  <div className="bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 group-hover:-translate-y-1">
                    <div className="p-3 bg-gradient-to-r from-[#1a2a5e] to-[#2d4a8e]">
                      <h4 className="text-white text-center font-semibold text-base">Marksheet Sample</h4>
                    </div>
                    <a href="https://customer-assets-39nsmqrw.emergentagent.net/job_87c0864c-f08f-4236-ae3c-b73a794502ae/artifacts/7b2oja3t_Sample%20Marksheet.png" target="_blank" rel="noopener noreferrer">
                      <img 
                        src="https://customer-assets-39nsmqrw.emergentagent.net/job_87c0864c-f08f-4236-ae3c-b73a794502ae/artifacts/7b2oja3t_Sample%20Marksheet.png" 
                        alt="Sample Marksheet - Medhavi Skills University" 
                        className="w-full h-auto object-contain p-4"
                        loading="lazy"
                      />
                    </a>
                  </div>
                </div>
              </div>
              <div className="text-center mt-8">
                <a 
                  href="https://stewardlms.com/verify-certificate" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="inline-flex items-center gap-2 bg-[#2eb24c] text-white px-8 py-3 rounded-lg font-semibold hover:bg-[#259a3e] transition-colors shadow-lg hover:shadow-xl"
                  data-testid="verify-certificate-btn"
                >
                  Verify Certificate
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                </a>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Brochures Section */}
      {brochures.length > 0 && (
        <section className="py-16 bg-slate-50" data-testid="brochures-section">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-10">
              <span className="text-[#2eb24c] font-semibold text-sm uppercase tracking-wider">Resources</span>
              <h2 className="font-manrope text-3xl md:text-4xl font-bold text-[#1a2a5e] mt-2">
                Download Brochures
              </h2>
              <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
                Get detailed information about our services and solutions
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {brochures.map((brochure) => (
                <Card key={brochure.id} className="border-0 shadow-lg hover:shadow-xl transition-shadow group">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0 group-hover:bg-red-100 transition-colors">
                        <FileText className="h-6 w-6 text-red-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-[#1a2a5e] mb-1">{brochure.title}</h3>
                        {brochure.description && (
                          <p className="text-gray-500 text-sm mb-3 line-clamp-2">{brochure.description}</p>
                        )}
                        <Button asChild size="sm" className="bg-[#1a2a5e] hover:bg-[#2d4a8e]" data-testid={`download-brochure-${brochure.id}`}>
                          <a
                            href={brochure.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => axios.post(`${API_URL}/brochures/${brochure.id}/download`).catch(() => {})}
                          >
                            <Download className="mr-2 h-4 w-4" />
                            Download PDF
                          </a>
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 cta-gradient text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-manrope text-3xl md:text-4xl font-bold mb-6">
            Ready to Transform Your Healthcare Facility?
          </h2>
          <p className="text-blue-100 text-lg mb-8 max-w-2xl mx-auto">
            Get in touch with our experts today and take the first step towards healthcare excellence.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
            <Button asChild size="lg" className="bg-[#2eb24c] hover:bg-[#259a40] h-14 px-8">
              <Link to="/contact" data-testid="cta-consultation-btn">
                Schedule Free Consultation
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="h-14 px-8 border-2 border-white text-white hover:bg-white hover:text-[#1a2a5e]">
              <a href="tel:+919616298162">
                <Phone className="mr-2 h-5 w-5" />
                Call Now
              </a>
            </Button>
          </div>
          <p className="text-blue-100">
            Or email us at: <a href="mailto:info@stewardindia.com" className="text-white font-semibold underline hover:text-[#2eb24c]">info@stewardindia.com</a>
          </p>
        </div>
      </section>
    </div>
  );
};

export default Home;
